import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export interface RecentPayment {
  id: string;
  user_name: string;
  amount: number;
  status: string;
  gateway: string;
  paid_at: string;
}

export interface RecentSubscription {
  id: string;
  user_name: string;
  plan_name: string;
  status: string;
  started_at: string;
}

export interface AdminAlert {
  type: 'warning' | 'error' | 'info';
  message: string;
}

export interface AdminStats {
  totalUsers: number;
  totalAdmins: number;
  activeUsers: number;
  freeUsers: number;
  paidUsers: number;
  trialUsers: number;
  newLast30Days: number;
  pendingPayments: number;
  cancelledSubscriptions: number;
  mrr: number;
  monthRevenue: number;
  defaulters: number;
  loginsToday: number;
  recentPayments: RecentPayment[];
  recentSubscriptions: RecentSubscription[];
  alerts: AdminAlert[];
  dailyNewUsers: { date: string; count: number }[];
}

const ADMIN_ROLE_VALUES = ['master_admin', 'admin', 'support', 'finance'];

export const useAdminStats = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<AdminStats>({
    totalUsers: 0,
    totalAdmins: 0,
    activeUsers: 0,
    freeUsers: 0,
    paidUsers: 0,
    trialUsers: 0,
    newLast30Days: 0,
    pendingPayments: 0,
    cancelledSubscriptions: 0,
    mrr: 0,
    monthRevenue: 0,
    defaulters: 0,
    loginsToday: 0,
    recentPayments: [],
    recentSubscriptions: [],
    alerts: [],
    dailyNewUsers: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      // Guard: only fetch if user is authenticated (admin route, but still safe)
      if (!user) { setLoading(false); return; }
      try {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const startOfMonth = new Date();
        startOfMonth.setDate(1);
        startOfMonth.setHours(0, 0, 0, 0);
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);

        // 1) Get all admin user_ids to exclude them from client counts
        const { data: adminRoles } = await supabase
          .from('user_roles')
          .select('user_id, role, status')
          .in('role', ADMIN_ROLE_VALUES);

        const adminUserIds = new Set((adminRoles || []).map((r: any) => r.user_id));
        const totalAdmins = (adminRoles || []).length;

        console.log('[AdminStats] user_roles admin rows:', adminRoles);
        console.log('[AdminStats] adminUserIds:', [...adminUserIds]);

        // 2) Remaining queries in parallel
        const [profilesRes, subsRes, paymentsRes, recentPaymentsRes, recentSubsRes, loginsTodayRes] =
          await Promise.all([
            supabase.from('profiles').select('id, is_active, is_blocked, plan, created_at'),
            supabase.from('subscriptions').select('user_id, status, subscription_type, amount'),
            supabase.from('payments').select('status, amount, paid_at'),
            supabase
              .from('payments')
              .select('id, user_id, amount, status, gateway, paid_at, profiles(full_name)')
              .order('created_at', { ascending: false })
              .limit(5),
            supabase
              .from('subscriptions')
              .select('id, user_id, status, started_at, profiles(full_name), plans(name)')
              .order('created_at', { ascending: false })
              .limit(5),
            supabase
              .from('audit_logs')
              .select('id', { count: 'exact', head: true })
              .eq('action', 'login')
              .gte('created_at', startOfToday.toISOString()),
          ]);

        console.log('[AdminStats] profilesRes.data:', profilesRes.data);
        console.log('[AdminStats] profilesRes.error:', profilesRes.error);
        console.log('[AdminStats] subsRes.data:', subsRes.data);
        console.log('[AdminStats] subsRes.error:', subsRes.error);

        // Filter out admins from profiles
        const allProfiles = profilesRes.data || [];
        const clientProfiles = allProfiles.filter((p: any) => !adminUserIds.has(p.id));

        console.log('[AdminStats] allProfiles count:', allProfiles.length);
        console.log('[AdminStats] clientProfiles count:', clientProfiles.length);

        // Filter subscriptions to clients only
        const allSubs = subsRes.data || [];
        const clientSubs = allSubs.filter((s: any) => !adminUserIds.has(s.user_id));

        const payments = paymentsRes.data || [];

        const totalUsers = clientProfiles.length;
        const activeUsers = clientProfiles.filter(
          (p: any) => p.is_active !== false && !p.is_blocked
        ).length;
        // Use subscription data for type counts
        const freeUsers = clientSubs.filter((s: any) => s.subscription_type === 'free' || !s.subscription_type).length;
        const paidUsers = clientSubs.filter((s: any) => s.subscription_type === 'paid').length;
        const trialUsers = clientSubs.filter((s: any) => s.subscription_type === 'trial' || s.status === 'trial').length;
        const newLast30Days = clientProfiles.filter(
          (p: any) => new Date(p.created_at) >= thirtyDaysAgo
        ).length;

        // Build daily new users for the last 30 days
        const dailyMap: Record<string, number> = {};
        for (let i = 29; i >= 0; i--) {
          const d = new Date();
          d.setDate(d.getDate() - i);
          const key = d.toISOString().slice(0, 10);
          dailyMap[key] = 0;
        }
        clientProfiles.forEach((p: any) => {
          if (!p.created_at) return;
          const key = new Date(p.created_at).toISOString().slice(0, 10);
          if (key in dailyMap) dailyMap[key] += 1;
        });
        const dailyNewUsers = Object.entries(dailyMap).map(([date, count]) => ({ date, count }));

        const cancelledSubscriptions = clientSubs.filter((s: any) => s.status === 'cancelled').length;
        const defaulters = clientSubs.filter((s: any) => s.status === 'defaulter').length;
        const pendingPayments = payments.filter((p: any) => p.status === 'pending').length;

        const loginsToday = loginsTodayRes.count ?? 0;

        // Real MRR: sum of active paid subscription amounts
        const mrr = clientSubs
          .filter((s: any) => s.status === 'active' && s.subscription_type === 'paid')
          .reduce((acc: number, s: any) => acc + (Number(s.amount) || 0), 0);

        const monthRevenue = payments
          .filter(
            (p: any) =>
              p.status === 'paid' &&
              p.paid_at &&
              new Date(p.paid_at) >= startOfMonth
          )
          .reduce((acc: number, p: any) => acc + (p.amount || 0), 0);

        const recentPayments: RecentPayment[] = ((recentPaymentsRes.data as any[]) || []).map(
          (p) => ({
            id: p.id,
            user_name: (p.profiles as any)?.full_name || '—',
            amount: p.amount || 0,
            status: p.status,
            gateway: p.gateway || '—',
            paid_at: p.paid_at,
          })
        );

        const recentSubscriptions: RecentSubscription[] = (
          (recentSubsRes.data as any[]) || []
        ).map((s) => ({
          id: s.id,
          user_name: (s.profiles as any)?.full_name || '—',
          plan_name: (s.plans as any)?.name || '—',
          status: s.status,
          started_at: s.started_at,
        }));

        const alerts: AdminAlert[] = [];
        if (defaulters > 0)
          alerts.push({ type: 'error', message: `${defaulters} usuário(s) inadimplente(s)` });
        if (pendingPayments > 0)
          alerts.push({
            type: 'warning',
            message: `${pendingPayments} pagamento(s) pendente(s)`,
          });
        if (cancelledSubscriptions > 0)
          alerts.push({
            type: 'info',
            message: `${cancelledSubscriptions} assinatura(s) cancelada(s)`,
          });

        setStats({
          totalUsers,
          totalAdmins,
          activeUsers,
          freeUsers,
          paidUsers,
          trialUsers,
          newLast30Days,
          pendingPayments,
          cancelledSubscriptions,
          mrr,
          monthRevenue,
          defaulters,
          loginsToday,
          recentPayments,
          recentSubscriptions,
          alerts,
          dailyNewUsers,
        });
      } catch (err) {
        console.error('Error fetching admin stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [user]);

  return { stats, loading };
};
